import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Vehicle } from '../../core/models/vehicle.model';
import { Device } from '../../core/models/device.model';
import { VehicleService } from '../../core/services/vehicle.service';
import { DeviceService } from '../../core/services/device.service';
import { LookupService } from '../../core/services/lookup.service';
import { OrganisationService } from '../../core/services/organisation.service';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { StatusLookup } from '../../core/models/lookup.model';
import { Organisation } from '../../core/models/organisation.model';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-vehicle-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, ConfirmDialogComponent],
  templateUrl: './vehicle-management.component.html',
  styleUrls: ['./management-page.component.css']
})
export class VehicleManagementComponent implements OnInit {
  vehicles: Vehicle[] = [];
  filteredVehicles: Vehicle[] = [];
  allDevices: Device[] = [];
  availableDevices: Device[] = [];
  organisations: Organisation[] = [];
  statuses: StatusLookup[] = [];
  showForm = false;
  isEditing = false;
  selectedVehicle: Vehicle | null = null;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  searchTerm = '';
  vehiclePendingDeleteId: number | null = null;
  
  vehicleForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private vehicleService: VehicleService,
    private deviceService: DeviceService,
    private lookupService: LookupService,
    private organisationService: OrganisationService,
    private authService: AuthService
  ) {
    this.vehicleForm = this.fb.group({
      vehicleRegistration: ['', Validators.required],
      vehicleIdentificationNumber: ['', Validators.required],
      vehicleManufacturerMake: ['', Validators.required],
      vehicleManufacturerModel: ['', Validators.required],
      vehicleYear: [new Date().getFullYear(), Validators.required],
      vehicleSeatingCapacity: [0, Validators.required],
      vehicleMileage: [0],
      vehicleLabel: [''],
      organisationID: [null],
      statusID: [1],
      deviceID: [null]
    });
  }

  ngOnInit(): void {
    this.loadStatuses();
    this.loadOrganisations();
    this.loadVehicles();
    this.loadAvailableDevices();
  }

  loadStatuses(): void {
    this.lookupService.getStatuses().subscribe({
      next: statuses => {
        this.statuses = statuses;
      },
      error: error => {
        console.error('Error loading statuses:', error);
        this.statuses = [];
      }
    });
  }

  loadVehicles(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.vehicleService.getVehicles(this.getOrganisationScope()).subscribe({
      next: (vehicles) => {
        this.vehicles = vehicles;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading vehicles:', error);
        this.errorMessage = 'Unable to load vehicles from the API.';
        this.isLoading = false;
      }
    });
  }

  loadOrganisations(): void {
    const currentUser = this.authService.getCurrentUser();
    const role = normalizeUserRole(currentUser?.userRoleName || currentUser?.userRole || currentUser?.userRoleID);

    if (role === UserRole.OPERATOR) {
      this.organisations = currentUser?.organisationID
        ? [{
            organisationID: currentUser.organisationID,
            orgName: currentUser.organisation?.orgName || currentUser.organisation?.organisationName || currentUser.organisationName || 'My organisation'
          } as Organisation]
        : [];
      return;
    }

    this.organisationService.getOrganisations().subscribe({
      next: organisations => {
        this.organisations = [...organisations].sort((first, second) =>
          this.getOrganisationOptionLabel(first).localeCompare(this.getOrganisationOptionLabel(second))
        );
      },
      error: error => {
        console.error('Error loading organisations:', error);
        this.organisations = [];
      }
    });
  }

  loadAvailableDevices(): void {
    this.deviceService.getDevices().subscribe({
      next: (devices) => {
        this.allDevices = devices;
        this.updateAvailableDeviceOptions();
      },
      error: (error) => {
        console.error('Error loading devices:', error);
        this.allDevices = [];
        this.availableDevices = [];
      }
    });
  }

  updateAvailableDeviceOptions(): void {
    const selectedDeviceID = this.selectedVehicle?.deviceID;
    const options = this.allDevices.filter(device => !Number(device.vehicleID) || Number(device.deviceID) === Number(selectedDeviceID));

    if (selectedDeviceID && !options.some(device => Number(device.deviceID) === Number(selectedDeviceID))) {
      const selectedDeviceSerial = this.getResolvedVehicleDeviceSerial(this.selectedVehicle);
      options.push({
        deviceID: selectedDeviceID,
        deviceSerialNumber: selectedDeviceSerial || '',
        deviceModel: this.selectedVehicle?.deviceModel || this.selectedVehicle?.device?.deviceModel || 'Assigned device',
        deviceSIMID: null,
        primarySIMID: null,
        secondarySIMID: null,
        vehicleID: this.selectedVehicle?.vehicleID || null,
        vehicle: this.selectedVehicle || null
      } as Device);
    }

    this.availableDevices = options;
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const vehicles = !term
      ? [...this.vehicles]
      : this.vehicles.filter(vehicle => [
          vehicle.vehicleRegistration,
          vehicle.vehicleIdentificationNumber,
          vehicle.vehicleManufacturerMake,
          vehicle.vehicleManufacturerModel,
          vehicle.vehicleLabel,
          vehicle.vehicleMileage,
          vehicle.statusName,
          vehicle.organisationName,
          vehicle.deviceSerialNumber,
          vehicle.deviceModel
        ].some(value => String(value || '').toLowerCase().includes(term)));

    this.filteredVehicles = this.sortVehiclesByOrganisation(vehicles);
  }

  getVehicleStatusName(vehicle: Vehicle): string {
    return vehicle.statusName
      || this.statuses.find(status => status.statusID === Number(vehicle.statusID))?.statusName
      || (vehicle.statusID ? `Status ${vehicle.statusID}` : '-');
  }

  isVehicleActive(vehicle: Vehicle): boolean {
    const explicitStatusName = vehicle.statusName
      || this.statuses.find(status => status.statusID === Number(vehicle.statusID))?.statusName
      || '';
    const statusName = explicitStatusName.toLowerCase();
    if (statusName) {
      return statusName === 'active' || statusName === 'confirmed';
    }

    return Number(vehicle.statusID) === 1 || !!vehicle.isActive;
  }

  getVehicleOrganisationName(vehicle: Vehicle): string {
    return vehicle.organisationName
      || vehicle.organisation?.orgName
      || vehicle.organisation?.organisationName
      || (vehicle.organisationID ? `Organisation ${vehicle.organisationID}` : 'Not assigned');
  }

  getVehicleDeviceLabel(vehicle: Vehicle): string {
    const serialNumber = this.getResolvedVehicleDeviceSerial(vehicle);
    if (serialNumber) {
      return serialNumber;
    }

    return vehicle.deviceID ? 'Serial unavailable' : 'Not assigned';
  }

  getDeviceOptionLabel(device: Device): string {
    return this.getDeviceSerialNumber(device) || 'Serial unavailable';
  }

  getOrganisationOptionLabel(organisation: Organisation): string {
    return organisation.orgName || organisation.organisationName || `Organisation ${organisation.organisationID}`;
  }

  onSubmit(): void {
    if (this.vehicleForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      const vehicleData = this.buildVehiclePayload();
      
      if (this.isEditing && this.selectedVehicle) {
        this.vehicleService.updateVehicle(this.selectedVehicle.vehicleID, vehicleData)
          .subscribe({
            next: () => {
              this.successMessage = 'Vehicle updated.';
              this.isLoading = false;
              this.loadVehicles();
              this.resetForm();
            },
            error: error => this.handleSaveError(error)
          });
      } else {
        this.vehicleService.createVehicle(vehicleData)
          .subscribe({
            next: () => {
              this.successMessage = 'Vehicle created.';
              this.isLoading = false;
              this.loadVehicles();
              this.resetForm();
            },
            error: error => this.handleSaveError(error)
          });
      }
    }
  }

  editVehicle(vehicle: Vehicle): void {
    this.selectedVehicle = vehicle;
    this.isEditing = true;
    this.showForm = true;
    this.updateAvailableDeviceOptions();
    
    this.vehicleForm.patchValue({
      vehicleRegistration: vehicle.vehicleRegistration,
      vehicleIdentificationNumber: vehicle.vehicleIdentificationNumber,
      vehicleManufacturerMake: vehicle.vehicleManufacturerMake,
      vehicleManufacturerModel: vehicle.vehicleManufacturerModel,
      vehicleYear: vehicle.vehicleYear,
      vehicleSeatingCapacity: vehicle.vehicleSeatingCapacity,
      vehicleMileage: vehicle.vehicleMileage,
      vehicleLabel: vehicle.vehicleLabel,
      organisationID: vehicle.organisationID,
      statusID: vehicle.statusID,
      deviceID: vehicle.deviceID
    });
  }

  deleteVehicle(vehicleId: number): void {
    this.vehiclePendingDeleteId = vehicleId;
  }

  cancelDeleteVehicle(): void {
    this.vehiclePendingDeleteId = null;
  }

  confirmDeleteVehicle(): void {
    if (!this.vehiclePendingDeleteId) {
      return;
    }

    this.vehicleService.deleteVehicle(this.vehiclePendingDeleteId).subscribe({
      next: () => {
        this.successMessage = 'Vehicle deleted.';
        this.vehiclePendingDeleteId = null;
        this.loadVehicles();
      },
      error: error => {
        console.error('Error deleting vehicle:', error);
        this.errorMessage = 'Unable to delete vehicle.';
        this.vehiclePendingDeleteId = null;
      }
    });
  }

  resetForm(): void {
    this.vehicleForm.reset(this.defaultFormValue());
    this.showForm = false;
    this.isEditing = false;
    this.selectedVehicle = null;
    this.updateAvailableDeviceOptions();
  }

  startCreate(): void {
    this.selectedVehicle = null;
    this.isEditing = false;
    this.showForm = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.vehicleForm.reset(this.defaultFormValue());
    this.updateAvailableDeviceOptions();
  }

  private getOrganisationScope(): number | undefined {
    const currentUser = this.authService.getCurrentUser();
    const role = normalizeUserRole(currentUser?.userRoleName || currentUser?.userRole || currentUser?.userRoleID);
    return role === UserRole.OPERATOR && currentUser?.organisationID
      ? currentUser.organisationID
      : undefined;
  }

  private handleSaveError(error: any): void {
    console.error('Error saving vehicle:', error);
    this.errorMessage = error?.error?.message || error?.message || 'Unable to save vehicle.';
    this.isLoading = false;
  }

  private buildVehiclePayload(): Partial<Vehicle> {
    const value = this.vehicleForm.value;
    const now = new Date().toISOString();
    const selected = this.selectedVehicle;

    return {
      vehicleID: selected?.vehicleID || 0,
      vehicleIdentificationNumber: value.vehicleIdentificationNumber || '',
      vehicleRegistration: value.vehicleRegistration || '',
      vehicleManufacturerMake: value.vehicleManufacturerMake || '',
      vehicleManufacturerModel: value.vehicleManufacturerModel || '',
      vehicleYear: Number(value.vehicleYear || new Date().getFullYear()),
      vehicleSeatingCapacity: Number(value.vehicleSeatingCapacity || 0),
      vehicleGrossVehicleMass: Number(selected?.vehicleGrossVehicleMass || 0),
      vehicleEngineCapacity: Number(selected?.vehicleEngineCapacity || 0),
      vehicleMaxSpeed: Number(selected?.vehicleMaxSpeed || 0),
      vehicleGovernedSpeed: Number(selected?.vehicleGovernedSpeed || 0),
      vehicleMileage: Number(value.vehicleMileage ?? selected?.vehicleMileage ?? 0),
      vehicleLabel: value.vehicleLabel || selected?.vehicleLabel || value.vehicleRegistration || '',
      vehicleCreateDate: selected?.vehicleCreateDate || now,
      vehicleModifyDate: now,
      organisationID: this.toNullableNumber(value.organisationID ?? selected?.organisationID ?? this.getOrganisationScope()),
      statusID: Number(value.statusID || selected?.statusID || 1),
      deviceID: this.toNullableNumber(value.deviceID)
    };
  }

  private defaultFormValue(): Record<string, any> {
    return {
      vehicleRegistration: '',
      vehicleIdentificationNumber: '',
      vehicleManufacturerMake: '',
      vehicleManufacturerModel: '',
      vehicleYear: new Date().getFullYear(),
      vehicleSeatingCapacity: 0,
      vehicleMileage: 0,
      vehicleLabel: '',
      organisationID: this.getOrganisationScope() || null,
      statusID: 1,
      deviceID: null
    };
  }

  private toNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private sortVehiclesByOrganisation(vehicles: Vehicle[]): Vehicle[] {
    return [...vehicles].sort((first, second) => {
      const organisationCompare = this.getVehicleOrganisationName(first)
        .localeCompare(this.getVehicleOrganisationName(second));

      if (organisationCompare !== 0) {
        return organisationCompare;
      }

      const firstLabel = first.vehicleLabel || first.vehicleRegistration || '';
      const secondLabel = second.vehicleLabel || second.vehicleRegistration || '';
      return firstLabel.localeCompare(secondLabel);
    });
  }

  private getResolvedVehicleDeviceSerial(vehicle: Vehicle | null | undefined): string {
    if (!vehicle) {
      return '';
    }

    return vehicle.deviceSerialNumber
      || this.getDeviceSerialNumber(vehicle.device)
      || this.getDeviceSerialNumber(this.findDeviceByID(vehicle.deviceID))
      || '';
  }

  private getDeviceSerialNumber(device: any): string {
    return device?.deviceSerialNumber
      || device?.serialNumber
      || device?.deviceSerialNo
      || device?.serialNo
      || '';
  }

  private findDeviceByID(deviceID: number | null | undefined): Device | null {
    if (!deviceID) {
      return null;
    }

    return this.allDevices.find(device => Number(device.deviceID) === Number(deviceID)) || null;
  }
}
